/**
 * Error Fallback Component
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description User-friendly error UI with recovery actions
 * @implements FR-002, FR-003, FR-004
 * @author Peter Dobler
 * @created 2026-01-20
 */

import React, { ErrorInfo } from 'react';

/**
 * Props for ErrorFallback component
 */
export interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error;
  /** React error info with component stack */
  errorInfo?: ErrorInfo | null;
  /** Name of the component that errored */
  componentName?: string;
  /** Callback to reset the error boundary */
  onReset?: () => void;
  /** Whether to show the report issue button */
  showReportButton?: boolean;
  /** Whether this is a section error (smaller UI) */
  isSection?: boolean;
}

/**
 * Error Fallback Component
 * FR-002: User-friendly error message
 * FR-003: Recovery actions (Refresh, Go Back)
 * FR-004: Report issue functionality
 */
export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  componentName,
  onReset,
  showReportButton = true,
  isSection = false,
}) => {
  /**
   * Handle page refresh
   */
  const handleRefresh = (): void => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  /**
   * Handle go back navigation
   */
  const handleGoBack = (): void => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  /**
   * Handle report issue action
   * FR-004: Allow users to report issues
   */
  const handleReportIssue = (): void => {
    // Generate error report
    const report = {
      error: error.message,
      stack: error.stack,
      component: componentName,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    // Log to console for now
    console.log('[ErrorFallback] Report issue:', report);

    // TODO: Integrate with issue reporting system
    // For now, copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      alert('Error details copied to clipboard. Please paste in a support ticket.');
    }
  };

  // Section error has a smaller, inline UI
  if (isSection) {
    return (
      <div
        className="error-fallback-section"
        role="alert"
        aria-live="polite"
        style={{
          padding: '1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          textAlign: 'center',
        }}
      >
        <p style={{ color: '#991b1b', marginBottom: '0.5rem' }}>
          Something went wrong in this section.
        </p>
        <button
          onClick={handleRefresh}
          style={{
            padding: '0.25rem 0.75rem',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
          }}
          aria-label="Retry loading this section"
        >
          Retry
        </button>
      </div>
    );
  }

  // Full-page error UI
  return (
    <div
      className="error-fallback"
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#f9fafb',
      }}
    >
      <div
        style={{
          maxWidth: '32rem',
          textAlign: 'center',
        }}
      >
        {/* Error Icon */}
        <div
          style={{
            width: '4rem',
            height: '4rem',
            margin: '0 auto 1.5rem',
            backgroundColor: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-hidden="true"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Error Message */}
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '0.5rem',
          }}
        >
          Something went wrong
        </h1>
        <p
          style={{
            color: '#6b7280',
            marginBottom: '1.5rem',
          }}
        >
          We're sorry, but something unexpected happened. Please try one of the options below.
        </p>

        {/* Error Details (Development Only) */}
        {import.meta.env.MODE === 'development' && (
          <details
            style={{
              textAlign: 'left',
              backgroundColor: '#fef2f2',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontWeight: '500',
                color: '#991b1b',
              }}
            >
              Error Details
            </summary>
            <pre
              style={{
                marginTop: '0.5rem',
                fontSize: '0.75rem',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#7f1d1d',
              }}
            >
              {error.message}
              {error.stack && `\n\n${error.stack}`}
              {componentName && `\n\nComponent: ${componentName}`}
            </pre>
          </details>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {/* Refresh Button - FR-003 */}
          <button
            onClick={handleRefresh}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2563eb')}
            aria-label="Refresh the page"
          >
            Refresh Page
          </button>

          {/* Go Back Button - FR-003 */}
          <button
            onClick={handleGoBack}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Go back to previous page"
          >
            Go Back
          </button>

          {/* Report Issue Button - FR-004 */}
          {showReportButton && (
            <button
              onClick={handleReportIssue}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: 'none',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              aria-label="Report this issue"
            >
              Report Issue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
